import React, {
  useState,
  useCallback,
  Fragment,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { TextField, Loader, ClickOut, ContentSwitch } from 'Components';
import ToolTip from 'react-portal-tooltip';
import { getCharacter } from 'helpers/id';
import { ETRID_MANAGEMENT } from 'constants/apis';
import Fetch from 'helpers/fetch';
import { buildUrl } from 'helpers/misc';
import useDebounce from 'helpers/hooks/useDebounce';
import useDidUpdateEffect from 'helpers/hooks/useDidUpdateEffect';
import './SampleIDList.css';

const tooltipStyle = {
  style: {
    background: 'rgba(0,0,0,.8)',
    padding: 10,
    zIndex: 401,
    boxShadow: '0px 5px 3px rgba(0,0,0,.5)',
  },
};

const SampleIDListItem = ({ data, isLoading, onSelect, Matrices }) => {
  const sampleIDListRef = useRef();

  useEffect(() => {
    if (sampleIDListRef.current) {
      sampleIDListRef.current.parentElement.style.borderRadius = 0;
      sampleIDListRef.current.parentElement.style.borderColor = '#6d6d5e';
    }
  }, []);

  return (
    <div ref={sampleIDListRef} className="sampleId-list-item-container">
      <ul
        className="sampleId-list-item-container__list"
        style={{
          height:
            data.length <= 3
              ? '120px'
              : `${Math.max(17, Math.min(20, data.length)) * 10}px`,
        }}
      >
        {isLoading ? (
          <Loader isSmall isSolidWhite />
        ) : (
          <ContentSwitch index={Number(data.length === 0)}>
            <Fragment>
              {data.map((item) => (
                <li
                  onClick={() => onSelect(item)}
                  onKeyPress={() => onSelect(item)}
                  role="menuitem"
                  key={item.SampleID}
                  className="sampleId-list-item-container__list__item"
                >
                  <p>{item.SampleID}</p>
                  <p>{`${
                    Matrices.find((x) => x.MatrixCode === item.MatrixCode)
                      ? ` - ${
                          Matrices.find((x) => x.MatrixCode === item.MatrixCode)
                            .Description
                        }`
                      : ''
                  }`}
                  </p>
                </li>
              ))}
            </Fragment>

            <Fragment>
              <div className="sampleId-list-item-container__list__notfound">
                <p>No matches found</p>
              </div>
            </Fragment>
          </ContentSwitch>
        )}
      </ul>
    </div>
  );
};

SampleIDListItem.defaultProps = {
  data: [],
  onSelect: () => null,
};

SampleIDListItem.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  onSelect: PropTypes.func,
  Matrices: PropTypes.arrayOf(PropTypes.shape({})),
};

const OptimizedSampleIDListItem = React.memo((props) => (
  <SampleIDListItem {...props} />
));

const SampleIDList = ({
  sample,
  ReportAddressNumber,
  ClientNumber,
  onValueChange,
  row,
  isShelfLife,
  onSelect,
  hasSampleLimitExceeded,
  MAX_NUMBER_OF_SAMPLES,
  Matrices,
  isSearchActive,
  isDuplicateSample,
}) => {
  const id = `${getCharacter(row)}_sampleIDList`;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [active, setActive] = useState(false);
  const [isListDisabled, setIsListDisabled] = useState(false);

  const debouncedSampleID = useDebounce(sample.SampleId, 340);

  const inputRef = useRef(null);

  const fetchSampleIDs = useCallback(
    (SampleID) => {
      setIsLoading(true);
      setData([]);
      if (!active) setActive(true);
      const URL = `${ETRID_MANAGEMENT}/Samples`;
      const params = {
        SampleID,
        ReportAddressNumber,
        ClientNumber,
      };

      Fetch.Get(buildUrl(URL, params))
        .then((response) => response.json())
        .then((responseData) => {
          const parsedData = (responseData.DataList || []).map((item) => ({
            SampleID: item.SampleID,
            MatrixCode: item.MatrixCode,
            Id: item.Id,
          }));
          setData(parsedData);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    },
    [ClientNumber, ReportAddressNumber, active],
  );

  useDidUpdateEffect(() => {
    if (isSearchActive) {
      fetchSampleIDs(debouncedSampleID);
    }
  }, [debouncedSampleID]);

  const handleSampleIdChange = useCallback(
    (value) => {
      onValueChange(row, 'SampleId', value);
    },
    [onValueChange, row],
  );

  useEffect(() => {
    if (isListDisabled && sample.SampleId === '') {
      setIsListDisabled(false);
    }
  }, [sample.SampleId, isListDisabled]);

  return (
    <span id={id}>
      <TextField
        inputRef={inputRef}
        value={sample.SampleId}
        onChange={(e) => handleSampleIdChange(e.target.value)}
        onBlur={() => setActive(false)}
        hasError={sample.SampleId.length > 200}
        // ( hasSampleLimitExceeded ) is for limiting max 200 samples
        disabled={
          (isShelfLife && row > 0) ||
          (hasSampleLimitExceeded && row > MAX_NUMBER_OF_SAMPLES - 1)
        }
        className={isDuplicateSample(sample) ? 'text-field__warning' : ''}
      />
      {isSearchActive && data.length !== 0 && !isListDisabled && (
        <ClickOut onClickOut={() => setActive(false)}>
          <ToolTip
            position="bottom"
            align="right"
            tooltipTimeout={0}
            active={active && sample.SampleId !== ''}
            parent={`#${id}`}
            group="sampleID"
            tooltipStyle={tooltipStyle}
          >
            <OptimizedSampleIDListItem
              data={data}
              isLoading={isLoading}
              Matrices={Matrices}
              onSelect={(item) => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
                onSelect({
                  ...item,
                  MatrixCode:
                    (
                      Matrices.find((x) => x.MatrixCode === item.MatrixCode) ||
                      {}
                    ).MatrixCode || '',
                });
                setActive(false);
                setIsListDisabled(true);
              }}
            />
          </ToolTip>
        </ClickOut>
      )}
    </span>
  );
};

SampleIDList.defaultProps = {
  sample: {
    SampleId: '',
  },
  isDuplicateSample: false,
};

SampleIDList.propTypes = {
  sample: PropTypes.shape({
    SampleId: PropTypes.string,
  }),
  onValueChange: PropTypes.func,
  row: PropTypes.number,
  isShelfLife: PropTypes.bool,
  hasSampleLimitExceeded: PropTypes.bool,
  MAX_NUMBER_OF_SAMPLES: PropTypes.number,
  onSelect: PropTypes.func,
  ReportAddressNumber: PropTypes.number,
  ClientNumber: PropTypes.string,
  isSearchActive: PropTypes.bool,
  Matrices: PropTypes.arrayOf(PropTypes.shape({})),
  isDuplicateSample: PropTypes.func,
};

export default SampleIDList;



// WEBPACK FOOTER //
// ./src/components/SampleIDList/SampleIDList.jsx